<template>
  <div class="citation-sources">
    <button
      type="button"
      class="citation-sources__toggle"
      :aria-expanded="String(open)"
      @click="$emit('update:open', !open)"
    >
      <i
        class="fas fa-chevron-right citation-sources__chevron"
        :class="{ 'citation-sources__chevron--open': open }"
        aria-hidden="true"
      ></i>
      <span class="citation-sources__count">{{ count }}</span>
      <span class="citation-sources__summary">{{ summary }}</span>
    </button>
    <ol v-if="open" ref="list" class="citation-sources__list">
      <li
        v-for="group in groups"
        :key="group.key"
        class="citation-sources__group"
        :class="{ 'citation-sources__group--single': group.single }"
      >
        <div
          class="citation-sources__head"
          :class="{
            'citation-sources__head--cited': group.cite,
            'citation-sources__row--lit':
              group.cite && highlighted.includes(group.cite.n),
          }"
          :data-source="group.cite ? group.cite.n : null"
        >
          <span
            v-if="group.cite"
            class="citation-marker citation-sources__number"
            aria-hidden="true"
            >{{ group.cite.n }}</span
          >
          <div class="citation-sources__body">
            <span class="citation-sources__kind">{{ group.kind }}</span>
            <a
              v-if="group.url"
              class="citation-sources__label"
              v-bind="anchor(group.url)"
            >
              <i :class="iconOf(group)" aria-hidden="true"></i>
              <span>{{ group.shown }}</span>
            </a>
            <span
              v-else
              class="citation-sources__label citation-sources__label--plain"
            >
              <i :class="iconOf(group)" aria-hidden="true"></i>
              <span>{{ group.shown }}</span>
            </span>
            <span v-if="!group.single" class="citation-sources__count">
              {{ group.passages }}
              {{ group.passages === 1 ? "passage" : "passages" }} cited
            </span>
            <span v-if="group.count" class="citation-sources__count">
              <i class="fas fa-table" aria-hidden="true"></i>
              {{ group.count }} records
            </span>
            <template v-if="group.single && group.cite.excerpt">
              <span
                :ref="(el) => keepQuote(group.cite.key, el)"
                class="citation-sources__quote"
                :class="{
                  'citation-sources__quote--clamped': !expanded[group.cite.key],
                }"
              >
                “{{ group.cite.excerpt }}”
              </span>
              <button
                v-if="long[group.cite.key]"
                type="button"
                class="citation-sources__more"
                @click="toggle(group.cite.key)"
              >
                {{ expanded[group.cite.key] ? "Show less" : "Show more" }}
              </button>
            </template>
          </div>
          <div class="citation-sources__ways">
            <a
              v-if="group.url"
              class="citation-sources__open"
              v-bind="anchor(group.url)"
              >{{ openLabel(group) }}</a
            >
            <button
              v-if="group.cite"
              type="button"
              :class="
                group.cite.n === cameFrom
                  ? 'citation-sources__back'
                  : 'citation-sources__in'
              "
              :aria-label="
                group.cite.n === cameFrom
                  ? 'Back to where you were reading'
                  : 'Show where this is used in the answer'
              "
              @click="$emit('return', group.cite.n)"
            >
              <i class="fas fa-arrow-up" aria-hidden="true"></i>
              {{
                group.cite.n === cameFrom
                  ? "Back to where you were"
                  : "in answer"
              }}
            </button>
          </div>
        </div>
        <ol v-if="group.rows.length" class="citation-sources__rows">
          <li
            v-for="source in shownRows(group)"
            :key="source.key"
            :data-source="source.n"
            class="citation-sources__row"
            :class="{
              'citation-sources__row--lit': highlighted.includes(source.n),
            }"
          >
            <span
              class="citation-marker citation-sources__number"
              aria-hidden="true"
              >{{ source.n }}</span
            >
            <div class="citation-sources__body">
              <template v-if="source.excerpt">
                <span
                  :ref="(el) => keepQuote(source.key, el)"
                  class="citation-sources__quote"
                  :class="{
                    'citation-sources__quote--clamped': !expanded[source.key],
                  }"
                >
                  “{{ source.excerpt }}”
                </span>
                <button
                  v-if="long[source.key]"
                  type="button"
                  class="citation-sources__more"
                  @click="toggle(source.key)"
                >
                  {{ expanded[source.key] ? "Show less" : "Show more" }}
                </button>
              </template>
            </div>
            <button
              type="button"
              :class="
                source.n === cameFrom
                  ? 'citation-sources__back'
                  : 'citation-sources__in'
              "
              :aria-label="
                source.n === cameFrom
                  ? 'Back to where you were reading'
                  : 'Show where this is used in the answer'
              "
              @click="$emit('return', source.n)"
            >
              <i class="fas fa-arrow-up" aria-hidden="true"></i>
              {{
                source.n === cameFrom ? "Back to where you were" : "in answer"
              }}
            </button>
          </li>
        </ol>
        <button
          v-if="group.rows.length > SHOWN"
          type="button"
          class="citation-sources__all"
          :aria-expanded="String(Boolean(opened[group.key]))"
          @click="openGroup(group.key, !opened[group.key])"
        >
          {{
            opened[group.key]
              ? "Show fewer"
              : `Show all ${group.rows.length} passages`
          }}
        </button>
      </li>
    </ol>
  </div>
</template>

<script>
import { linkAttributes } from "@/components/OliverBotV2/utils/oliverLinks.js";
import { isWebPage, openLabel, sourcesSummary } from "./citations.js";

// Passages a source shows before "Show all".
const SHOWN = 3;

/** An answer's sources, each once, with the passages the answer cites from it under it; folded
 * until opened. */
export default {
  name: "CitationSources",
  props: {
    sources: { type: Array, required: true },
    open: { type: Boolean, default: false },
    highlighted: { type: Array, default: () => [] },
    cameFrom: { type: Number, default: null },
    linkTo: { type: Function, required: true },
  },
  emits: ["update:open", "return"],
  data() {
    return {
      // Quotes taller than their three lines, and the ones opened in full.
      long: {},
      expanded: {},
      // Sources showing all their passages rather than the first few.
      opened: {},
    };
  },
  created() {
    this.quotes = {};
    this.SHOWN = SHOWN;
  },
  computed: {
    /** One entry per source, in the order the answer first cites it. A source cited once is a
     * single line with its number and quote; a source cited more than once groups its quoted
     * passages under it, and a citation of it with nothing to quote — a set, a record cited
     * whole — puts its number on the source's own line. */
    groups() {
      const groups = new Map();
      for (const source of this.sources) {
        const key = source.sourceKey || source.key;
        if (!groups.has(key)) groups.set(key, { ...source, key, cited: [] });
        groups.get(key).cited.push(source);
      }
      return [...groups.values()].map(({ cited, ...group }) => {
        const single = cited.length === 1;
        return {
          ...group,
          single,
          cite: single ? cited[0] : cited.find((c) => !c.excerpt) || null,
          rows: single ? [] : cited.filter((c) => c.excerpt),
          passages: cited.filter((c) => c.sourceKey).length,
        };
      });
    },
    count() {
      const n = this.groups.length;
      return `${n} ${n === 1 ? "source" : "sources"}`;
    },
    summary() {
      return sourcesSummary(this.sources);
    },
  },
  watch: {
    open: "measure",
    sources: "measure",
    highlighted(ns) {
      if (!ns.length) return;
      // A passage past the first few opens its group, so the row it names is there to see.
      for (const n of ns) {
        const group = this.groups.find((g) =>
          g.rows.some((row) => row.n === n)
        );
        if (group && !this.shownRows(group).some((row) => row.n === n)) {
          this.openGroup(group.key, true);
        }
      }
      this.$nextTick(() =>
        this.$refs.list
          ?.querySelector(`[data-source="${ns[0]}"]`)
          ?.scrollIntoView({ block: "nearest", behavior: "smooth" })
      );
    },
  },
  methods: {
    keepQuote(key, el) {
      if (el) this.quotes[key] = el;
      else delete this.quotes[key];
    },
    /** Which quotes run past three lines, measured while every one is clamped. */
    measure() {
      this.expanded = {};
      this.long = {};
      this.measureClamped();
    },
    /** Measures the quotes still clamped, so opening a group keeps what was opened in full. */
    measureClamped() {
      this.$nextTick(() => {
        const long = { ...this.long };
        for (const [key, el] of Object.entries(this.quotes)) {
          if (!this.expanded[key])
            long[key] = el.scrollHeight > el.clientHeight + 1;
        }
        this.long = long;
      });
    },
    shownRows(group) {
      return this.opened[group.key] ? group.rows : group.rows.slice(0, SHOWN);
    },
    openGroup(key, open) {
      this.opened = { ...this.opened, [key]: open };
      this.measureClamped();
    },
    toggle(key) {
      this.expanded = { ...this.expanded, [key]: !this.expanded[key] };
    },
    iconOf(group) {
      return isWebPage(group) ? "fas fa-globe" : "fas fa-paperclip";
    },
    openLabel,
    // Routed like every link in an answer: MessageBubble's click handler follows an internal one.
    anchor(url) {
      return linkAttributes(this.linkTo(url));
    },
  },
};
</script>

<style lang="scss" scoped>
.citation-sources {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.08));

  &__toggle {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 8px;
    width: 100%;
    padding: 4px 0;
    border: 0;
    background: none;
    text-align: left;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    color: var(--oliver-text);
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--oliver-link-color, #2b6cb0);
      outline-offset: 2px;
    }
  }

  &__chevron {
    font-size: 11px;
    color: var(--oliver-text-muted);
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(90deg);
    }
  }

  &__count {
    white-space: nowrap;
  }

  &__summary {
    font-size: 13px;
    font-weight: 400;
    color: var(--oliver-text-muted);
  }

  &__list {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
    margin: 8px 0 0;
    padding: 0;
    list-style: none;
  }

  // A source: its kind, its name and the way to it, then the passages the answer cites.
  &__group {
    display: grid;
    gap: 4px;
    min-width: 0;
    padding: 8px 8px 6px;
    border: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.08));
    border-radius: 10px;
  }

  // A source cited once reads as one line, as before grouping: no frame to group anything in.
  &__group--single {
    border-color: transparent;
  }

  &__head {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 2px 8px;
  }

  &__head--cited {
    grid-template-columns: 26px minmax(0, 1fr);
    padding: 2px 6px 2px 0;
    border-radius: 8px;
    transition: background 0.2s ease;
  }

  // What to do with a source sits right under it, at every width: across a wide panel a
  // column of links on the far side could not be told apart from its neighbours'.
  &__ways {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0 14px;
  }

  &__head--cited &__ways {
    grid-column: 2;
  }

  &__open {
    font-size: 12px;
    font-weight: 600;
    color: var(--oliver-link-color, #2b6cb0);
    text-decoration: none;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
  }

  &__rows {
    display: grid;
    gap: 2px;
    margin: 4px 0 0;
    padding: 0;
    list-style: none;
  }

  &__all {
    justify-self: start;
    margin: 2px 0 2px 40px;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    color: var(--oliver-link-color, #2b6cb0);
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--oliver-link-color, #2b6cb0);
      outline-offset: 2px;
    }
  }

  &__row {
    display: grid;
    grid-template-columns: 26px minmax(0, 1fr);
    gap: 8px;
    padding: 6px;
    border-radius: 8px;
    transition: background 0.2s ease;

    &--lit {
      background: rgba(99, 179, 237, 0.12);
    }
  }

  // The way back from a row: to the number the reader clicked, or where a source is used.
  &__back,
  &__in {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    grid-column: 2;
    justify-self: start;
    min-height: 32px;
    padding: 0 4px 0 0;
    border: 0;
    background: none;
    font: inherit;
    font-size: 12px;
    white-space: nowrap;
    cursor: pointer;

    i {
      font-size: 10px;
    }

    &:hover {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid var(--oliver-link-color, #2b6cb0);
      outline-offset: 2px;
    }
  }

  &__back {
    font-weight: 600;
    color: var(--oliver-link-color, #2b6cb0);
  }

  &__in {
    color: var(--oliver-text-muted);
    opacity: 0.85;
  }

  &__number {
    justify-self: start;
    cursor: default;
  }

  &__body {
    display: grid;
    gap: 2px;
    min-width: 0;
  }

  &__kind {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: var(--oliver-text-muted);
  }

  &__label {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--oliver-link-color, #2b6cb0);
    text-decoration: none;
    overflow-wrap: anywhere;

    i {
      font-size: 10px;
    }

    &:hover {
      text-decoration: underline;
    }

    &--plain,
    &--plain:hover {
      color: var(--oliver-text-muted);
      text-decoration: none;
    }
  }

  &__count {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    font-size: 12.5px;
    color: var(--oliver-text-muted);

    i {
      font-size: 10px;
    }
  }

  &__quote {
    overflow-wrap: anywhere;
    font-size: 12.5px;
    font-style: italic;
    color: var(--oliver-text-muted);

    &--clamped {
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  &__more {
    justify-self: start;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    color: var(--oliver-link-color, #2b6cb0);
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--oliver-link-color, #2b6cb0);
      outline-offset: 2px;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .citation-sources__row,
  .citation-sources__chevron {
    transition: none;
  }
}
</style>
